import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, MapLocKeys } from '@cm/core/localization';

const sharedProps: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
	feature: HubFeature.OtherRequirement,
};

const pathStart = 'Catalogs|Other Requirements|Advanced|';

export type SPOtherRequirementsAdvancedLocNames =
	| 'SPOtherRequirementsAdvancedPageTitle'
	| 'SPOtherRequirementsAdvancedSectionTitle'
	| 'SPOtherRequirementsAdvancedTitleCurrentSubdivisions'
	| 'SPOtherRequirementsAdvancedLabelName'
	| 'SPOtherRequirementsAdvancedLabelStartDate'
	| 'SPOtherRequirementsAdvancedLabelEndDate'
	| 'SPOtherRequirementsAdvancedLabelPoints'
	| 'SPOtherRequirementsAdvancedLabelMaxPoints'
	| 'SPOtherRequirementsAdvancedLabelCreateAge'
	| 'SPOtherRequirementsAdvancedLabelValidPeriod'
	| 'SPOtherRequirementsAdvancedLabelFactor'
	| 'SPOtherRequirementsAdvancedLabelGroup'
	| 'SPOtherRequirementsAdvancedButtonAddSubdivision'
	| 'SPOtherRequirementsAdvancedButtonAddGroup'
	| 'SPOtherRequirementsAdvancedButtonSave'
	| 'SPOtherRequirementsAdvancedButtonCancel'
	| 'SPOtherRequirementsAdvancedButtonSubmit'
	| 'SPOtherRequirementsAdvancedLabelAddSubdivision'
	| 'SPOtherRequirementsAdvancedLabelAddGroup'
	| 'SPOtherRequirementsAdvancedNotificationSubdivisionSuccess'
	| 'SPOtherRequirementsAdvancedNotificationSubdivisionFailed'
	| 'SPOtherRequirementsAdvancedNotificationGroupRequired';

export const spOtherRequirementsAdvancedLocs: LocDefs<SPOtherRequirementsAdvancedLocNames> = {
	SPOtherRequirementsAdvancedPageTitle: {
		defaultValue: 'Other Requirements',
		description: 'The title of the page for viewing other requirements details.',
		displayPath: `${pathStart}Title Page`,
		displayType: 'Title',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedSectionTitle: {
		defaultValue: 'Current Subdivisions',
		description: 'The title of the section for viewing subdivisions.',
		displayPath: `${pathStart}Section Title`,
		displayType: 'Title',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedTitleCurrentSubdivisions: {
		defaultValue: 'Current Subdivisions',
		description: 'The title for the current subdivisions table.',
		displayPath: `${pathStart}Title Current Subdivisions`,
		displayType: 'Title',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelName: {
		defaultValue: 'Name',
		description: 'The label for column heading: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'The label for column heading: Start Date.',
		displayPath: `${pathStart}Start Date`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelEndDate: {
		defaultValue: 'End Date',
		description: 'The label for column heading: End Date.',
		displayPath: `${pathStart}End Date`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelPoints: {
		defaultValue: 'Points',
		description: 'The label for column heading: Points.',
		displayPath: `${pathStart}Points`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelMaxPoints: {
		defaultValue: 'Max Points',
		description: 'The label for column heading: Max Points.',
		displayPath: `${pathStart}Max Points`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelCreateAge: {
		defaultValue: 'Create Age',
		description: 'The label for column heading: Create Age.',
		displayPath: `${pathStart}Create Age`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelValidPeriod: {
		defaultValue: 'Valid Period',
		description: 'The label for column heading: Valid Period.',
		displayPath: `${pathStart}Valid Period`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelFactor: {
		defaultValue: 'Factor',
		description: 'The label for column heading: Factor.',
		displayPath: `${pathStart}Factor`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelGroup: {
		defaultValue: 'Group',
		description: 'The label for column heading: Group.',
		displayPath: `${pathStart}Group`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedButtonAddSubdivision: {
		defaultValue: 'Add Subdivision',
		description: 'The button text for adding a new subdivision.',
		displayPath: `${pathStart}Button Add Subdivision`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedButtonAddGroup: {
		defaultValue: 'Add Group',
		description: 'The button text for adding a new group.',
		displayPath: `${pathStart}Button Add Group`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedButtonSave: {
		defaultValue: 'Save',
		description: 'The button text for saving.',
		displayPath: `${pathStart}Button Save`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedButtonCancel: {
		defaultValue: 'Cancel',
		description: 'The button text for canceling.',
		displayPath: `${pathStart}Button Cancel`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedButtonSubmit: {
		defaultValue: 'Submit',
		description: 'The button text for submitting the form.',
		displayPath: `${pathStart}Button Submit`,
		displayType: 'Button',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelAddSubdivision: {
		defaultValue: 'Add Subdivision',
		description: 'The title for the add subdivision form.',
		displayPath: `${pathStart}Label Add Subdivision`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedLabelAddGroup: {
		defaultValue: 'Add Group',
		description: 'The title for the add group modal.',
		displayPath: `${pathStart}Label Add Group`,
		displayType: 'Label',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedNotificationSubdivisionSuccess: {
		defaultValue: 'Subdivision has been created successfully.',
		description: 'Success message when a subdivision is created.',
		displayPath: `${pathStart}Notification Subdivision Success`,
		displayType: 'Notification',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedNotificationSubdivisionFailed: {
		defaultValue: 'Failed to create subdivision. Please try again.',
		description: 'Error message when subdivision creation fails.',
		displayPath: `${pathStart}Notification Subdivision Failed`,
		displayType: 'Notification',
		...sharedProps,
	},
	SPOtherRequirementsAdvancedNotificationGroupRequired: {
		defaultValue: 'Group is required to create a subdivision.',
		description: 'Error message when no group is selected.',
		displayPath: `${pathStart}Notification Group Required`,
		displayType: 'Notification',
		...sharedProps,
	},
};

export const LOC_KEYS = MapLocKeys<SPOtherRequirementsAdvancedLocNames>(
	spOtherRequirementsAdvancedLocs
);
